import { useEffect, useRef, useState } from "react";
import { FiMenu, FiX } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/ALTAIR-01.png";

const navLinks = [
  { name: "Home", to: "/" },
  { name: "About Us", to: "/about" },
  {
    name: "Services",
    dropdown: true,
    items: [
      { name: "Construction & Civil Works", to: "/construction" },
      { name: "ICT & IoT Services", to: "/ict" },
      { name: "Electrical Services", to: "/electrical" },
      { name: "Fire Systems", to: "/fire" },
    ],
  },
  { name: "Projects", to: "/projects" },
];

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [headerText, setHeaderText] = useState("text-black");
  const location = useLocation();
  const dropdownRef = useRef(null);

  const phoneNumber = "255714183424";

  // Close desktop dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
    }
    if (servicesOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [servicesOpen]);

  // Determine active link
  const getActive = () => {
    const path = location.pathname;
    if (path === "/") return "Home";
    const found = navLinks.find(
      (l) =>
        l.to === path ||
        (l.dropdown && l.items.some((item) => item.to === path))
    );
    if (found) return found.name;
    return "";
  };
  const active = getActive();

  return (
    <header
      className={`w-full sticky top-0 left-0 z-50 transition-colors duration-500 bg-white ${headerText} font-sans shadow-lg`}
      style={{ fontFamily: "'Inter', 'Poppins', 'Nunito Sans', sans-serif" }}
      aria-label="Site Header"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Left: Logo */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <Link
              to="/"
              className="flex items-center"
              onClick={() => setMenuOpen(false)}
            >
              <img
                src={logo}
                alt="Altair Logo"
                className="h-10 w-auto object-contain"
                draggable="false"
              />
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex flex-1 justify-center items-center space-x-6">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name} className="relative" ref={dropdownRef}>
                  <button
                    className={`flex items-center gap-1 px-2 py-1 font-medium transition focus:outline-none ${
                      active === link.name
                        ? "text-[#FF6B00] underline underline-offset-8"
                        : "hover:text-[#FF6B00]"
                    }`}
                    aria-haspopup="true"
                    aria-expanded={servicesOpen}
                    onClick={() => setServicesOpen((open) => !open)}
                  >
                    {link.name}
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {servicesOpen && (
                    <div
                      className="absolute left-1/2 -translate-x-1/2 mt-3 w-60 bg-white border border-gray-200 rounded-md shadow-lg py-2 z-50 animate-fade-in"
                      role="menu"
                    >
                      {link.items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.to}
                          className="block px-4 py-2 text-sm text-black hover:bg-[#FF6B00]/10 hover:text-[#FF6B00] transition"
                          role="menuitem"
                          onClick={() => setServicesOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.to}
                  className={`px-2 py-1 font-medium transition ${
                    active === link.name
                      ? "text-[#FF6B00] underline underline-offset-8"
                      : "hover:text-[#FF6B00]"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* Desktop Right: Contact */}
          <div className="hidden md:flex items-center gap-2 ml-2">
            <a
              href={`https://wa.me/${phoneNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-3 rounded-full shadow-lg flex items-center space-x-2 hover:bg-white hover:text-orange-600 transition"
            >
              <FaWhatsapp className="w-5 h-5" />
              <span>Chat</span>
            </a>

            <Link
              to="/contact"
              className="ml-1 px-4 py-2 rounded-xl bg-black text-white font-semibold hover:bg-white hover:border hover:border-orange-600 hover:text-orange-600 border-2 transition"
            >
              Contact
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMenuOpen((open) => !open)}
              className="inline-flex items-center justify-center p-2 rounded-md focus:outline-none"
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? (
                <FiX className="h-7 w-7" />
              ) : (
                <FiMenu className="h-7 w-7" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Overlay */}
        <div
          className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${
            menuOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={!menuOpen}
          onClick={() => setMenuOpen(false)}
        />

        {/* Mobile Menu */}
        <aside
          className={`fixed top-0 right-0 h-full w-72 bg-white shadow-lg z-50 transform transition-transform duration-300 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
          aria-label="Mobile menu"
        >
          <div className="flex items-center justify-between px-4 py-4 border-b">
            <Link to="/" onClick={() => setMenuOpen(false)}>
              <img src={logo} alt="Altair Logo" className="h-8 w-auto" />
            </Link>
            <button
              onClick={() => setMenuOpen(false)}
              className="p-2 rounded-full hover:bg-gray-100"
              aria-label="Close menu"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          <nav className="flex flex-col gap-1 mt-4 px-4">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name} className="relative">
                  <button
                    className={`flex items-center w-full px-2 py-2 font-medium transition ${
                      active === link.name
                        ? "text-[#FF6B00] underline underline-offset-8"
                        : "hover:text-[#FF6B00]"
                    }`}
                    onClick={() => setMobileServicesOpen((prev) => !prev)}
                    aria-haspopup="true"
                    aria-expanded={mobileServicesOpen}
                  >
                    {link.name}
                    <svg
                      className="w-4 h-4 ml-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {mobileServicesOpen && (
                    <div className="pl-4">
                      {link.items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.to}
                          className="block px-4 py-2 text-sm text-black hover:bg-[#FF6B00]/10 hover:text-[#FF6B00] transition"
                          onClick={() => {
                            setMenuOpen(false);
                            setMobileServicesOpen(false);
                          }}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.to}
                  className={`block px-2 py-2 font-medium transition ${
                    active === link.name
                      ? "text-[#FF6B00] underline underline-offset-8"
                      : "hover:text-[#FF6B00]"
                  }`}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.name}
                </Link>
              )
            )}

            {/* Quick Call & Contact */}
            <div className="flex items-center gap-2 mt-4">
              <Link
                to="/contact"
                className="ml-1 px-4 py-2 rounded-full bg-black text-white font-semibold hover:text-orange-600 bottom-2 hover:border-orange-600 hover:bg-white transition"
                onClick={() => setMenuOpen(false)}
              >
                Contact
              </Link>
            </div>
          </nav>
        </aside>
      </nav>
    </header>
  );
}

export default Header;
